/**
 * Tracking en vivo económico — Movex.
 * GPS + Firebase + interpolación local. Sin Google Routes para animación.
 */
import { GetDistance } from './GeoFunctions';
import {
    LOCATION_UPDATE_MIN_DISTANCE_ACTIVE_METERS,
    LOCATION_UPDATE_MIN_DISTANCE_IDLE_METERS,
    LOCATION_UPDATE_MIN_DISTANCE_SEARCHING_METERS,
    LOCATION_UPDATE_INTERVAL_ACTIVE_MS,
    LOCATION_UPDATE_INTERVAL_IDLE_MS,
    LOCATION_UPDATE_INTERVAL_SEARCHING_MS,
    LOCATION_SMOOTH_ANIMATION_MS,
    LOCATION_SMOOTH_TICK_MS,
    LOCATION_FRESH_MAX_AGE_MS,
    ROUTE_RECALC_DEVIATION_METERS,
} from '../config/economyConfig';
import { encodeGeohash } from './NearbyDriverSearch';

export const LOCATION_IDLE_MIN_DISTANCE_METERS = LOCATION_UPDATE_MIN_DISTANCE_IDLE_METERS;
export const LOCATION_SEARCHING_MIN_DISTANCE_METERS = LOCATION_UPDATE_MIN_DISTANCE_SEARCHING_METERS;
export const LOCATION_ACTIVE_MIN_DISTANCE_METERS = LOCATION_UPDATE_MIN_DISTANCE_ACTIVE_METERS;
export { LOCATION_FRESH_MAX_AGE_MS, ROUTE_RECALC_DEVIATION_METERS };
export const DEFAULT_TRACKING_SPEED_KMH = 40;
export const CITY_ETA_SPEED_KMH = 30;
export const ROAD_ETA_SPEED_KMH = 45;

/** ETA sin Google Routes: usa velocidad GPS si es fiable, si no urbano/carretera por distancia. */
export const resolveEtaSpeedKmh = (distanceKm, speedMsOrKmh) => {
    const km = Math.max(0, parseFloat(distanceKm) || 0);
    let speed = parseFloat(speedMsOrKmh);
    if (Number.isFinite(speed) && speed > 0) {
        if (speed < 25) speed = speed * 3.6;
        if (speed >= 8 && speed <= 120) return speed;
    }
    return km > 8 ? ROAD_ETA_SPEED_KMH : CITY_ETA_SPEED_KMH;
};

export const getApproxEta = (distanceKm, speedKmh = DEFAULT_TRACKING_SPEED_KMH) => {
    const km = Math.max(0, parseFloat(distanceKm) || 0);
    const speed = Math.max(5, parseFloat(speedKmh) || DEFAULT_TRACKING_SPEED_KMH);
    return Math.max(1, Math.round((km / speed) * 60));
};

export const calculateHaversineDistanceKm = (origin, destination) => {
    const lat1 = typeof origin === 'object' ? (origin.lat ?? origin.latitude) : parseFloat(String(origin).split(',')[0]);
    const lng1 = typeof origin === 'object' ? (origin.lng ?? origin.longitude) : parseFloat(String(origin).split(',')[1]);
    const lat2 = typeof destination === 'object' ? (destination.lat ?? destination.latitude) : parseFloat(String(destination).split(',')[0]);
    const lng2 = typeof destination === 'object' ? (destination.lng ?? destination.longitude) : parseFloat(String(destination).split(',')[1]);
    if (!Number.isFinite(lat1) || !Number.isFinite(lng1) || !Number.isFinite(lat2) || !Number.isFinite(lng2)) {
        return 0;
    }
    return GetDistance(lat1, lng1, lat2, lng2);
};

export const calculateBearing = (origin, destination) => {
    const lat1 = (typeof origin.latitude === 'number' ? origin.latitude : origin.lat) * Math.PI / 180;
    const lat2 = (typeof destination.latitude === 'number' ? destination.latitude : destination.lat) * Math.PI / 180;
    const dLng = ((typeof destination.longitude === 'number' ? destination.longitude : destination.lng)
        - (typeof origin.longitude === 'number' ? origin.longitude : origin.lng)) * Math.PI / 180;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return normalizeHeading((Math.atan2(y, x) * 180) / Math.PI);
};

export const normalizeHeading = (heading) => {
    const h = parseFloat(heading);
    if (!Number.isFinite(h)) return 0;
    return ((h % 360) + 360) % 360;
};

export const resolveVehicleHeading = (current, previous, gpsHeading) => {
    if (gpsHeading != null && Number.isFinite(parseFloat(gpsHeading)) && parseFloat(gpsHeading) >= 0) {
        return normalizeHeading(gpsHeading);
    }
    if (current && previous) {
        return calculateBearing(previous, current);
    }
    return 0;
};

export const shouldUpdateLocation = (previousLocation, newLocation, options = {}) => {
    const mode = options.mode || (options.isActiveService ? 'active' : 'idle');
    const minDistanceMeters = options.minDistanceMeters ?? (
        mode === 'active'
            ? LOCATION_ACTIVE_MIN_DISTANCE_METERS
            : mode === 'searching'
                ? LOCATION_SEARCHING_MIN_DISTANCE_METERS
                : LOCATION_IDLE_MIN_DISTANCE_METERS
    );
    const minIntervalMs = options.minIntervalMs ?? (
        mode === 'active'
            ? LOCATION_UPDATE_INTERVAL_ACTIVE_MS
            : mode === 'searching'
                ? LOCATION_UPDATE_INTERVAL_SEARCHING_MS
                : LOCATION_UPDATE_INTERVAL_IDLE_MS
    );

    if (!newLocation || newLocation.lat == null || newLocation.lng == null) return false;
    if (!previousLocation || previousLocation.lat == null) return true;

    const distKm = calculateHaversineDistanceKm(previousLocation, newLocation);
    const elapsed = Date.now() - (previousLocation.at || previousLocation.updatedAt || 0);
    return distKm >= (minDistanceMeters / 1000) || elapsed >= minIntervalMs;
};

export const interpolatePosition = (from, to, progress) => {
    const t = Math.min(1, Math.max(0, parseFloat(progress) || 0));
    const lat1 = from.lat ?? from.latitude;
    const lng1 = from.lng ?? from.longitude;
    const lat2 = to.lat ?? to.latitude;
    const lng2 = to.lng ?? to.longitude;
    return {
        latitude: lat1 + (lat2 - lat1) * t,
        longitude: lng1 + (lng2 - lng1) * t,
    };
};

export { LOCATION_SMOOTH_TICK_MS, LOCATION_SMOOTH_ANIMATION_MS };

/** Posición suavizada entre dos pings GPS (sin APIs de pago). */
export const getSmoothDriverCoord = (origin, target, startedAt, durationMs = LOCATION_SMOOTH_ANIMATION_MS) => {
    if (!target) return null;
    const toLat = target.lat ?? target.latitude;
    const toLng = target.lng ?? target.longitude;
    if (!Number.isFinite(toLat) || !Number.isFinite(toLng)) return null;
    if (!origin) return { latitude: toLat, longitude: toLng };
    const elapsed = Date.now() - (startedAt || Date.now());
    const progress = Math.min(1, elapsed / durationMs);
    return interpolatePosition(origin, { lat: toLat, lng: toLng }, progress);
};

export const isLocationFresh = (updatedAt, maxAgeMs = LOCATION_FRESH_MAX_AGE_MS) => {
    if (!updatedAt) return false;
    const ts = typeof updatedAt === 'number' ? updatedAt : Date.parse(updatedAt);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts <= maxAgeMs;
};

export const formatApproxDistanceText = (distanceKm, convertToMile = false) => {
    const km = parseFloat(distanceKm) || 0;
    const value = convertToMile ? km / 1.609344 : km;
    const unit = convertToMile ? 'mi' : 'km';
    return `Aprox. ${value.toFixed(1)} ${unit}`;
};

export const getTrackingStatus = (driverLocation, pickupLocation, dropLocation, bookingStatus) => {
    if (!driverLocation || driverLocation.lat == null) {
        return { key: 'updating', label: 'Ubicación actualizándose' };
    }
    if (!isLocationFresh(driverLocation.updatedAt)) {
        return { key: 'stale', label: 'Ubicación actualizándose' };
    }

    const target = bookingStatus === 'STARTED' ? dropLocation : pickupLocation;
    if (!target || target.lat == null) {
        return { key: 'on_way', label: 'Gruero en camino' };
    }

    const distKm = calculateHaversineDistanceKm(driverLocation, target);
    const etaSpeed = resolveEtaSpeedKmh(distKm, driverLocation.speed);
    const etaMins = getApproxEta(distKm, etaSpeed);
    const convertToMile = false;

    if (bookingStatus === 'ARRIVED') {
        return { key: 'arrived', label: 'Tu grúa ha llegado', distanceKm: distKm, etaMins: 0 };
    }
    if (distKm <= 0.3) {
        return { key: 'approaching', label: 'Se está acercando', distanceKm: distKm, etaMins: etaMins };
    }
    return {
        key: 'on_way',
        label: `Gruero a ${formatApproxDistanceText(distKm, convertToMile)} aprox.`,
        distanceKm: distKm,
        etaMins: etaMins,
    };
};

export const getLocationAgeLabel = (updatedAt) => {
    if (!updatedAt) return 'Ubicación actualizándose';
    const ts = typeof updatedAt === 'number' ? updatedAt : Date.parse(updatedAt);
    if (!Number.isFinite(ts)) return 'Ubicación actualizándose';
    const secs = Math.round((Date.now() - ts) / 1000);
    if (secs < 6) return 'En vivo';
    if (secs < 15) return 'Ubicación actualizada hace unos segundos';
    if (secs < 60) return `Ubicación actualizada hace ${secs} s`;
    const mins = Math.round(secs / 60);
    return `Ubicación actualizada hace ${mins} min`;
};

/**
 * Ruta real solo si Google Routes está habilitado, reason permitido y desvío grande.
 */
export const shouldRecalculateRoute = (context = {}) => {
    const {
        settings,
        routeReason,
        driverLocation,
        routePolylinePoints,
        lastRouteCalcAt,
        deviationMeters = ROUTE_RECALC_DEVIATION_METERS,
    } = context;

    if (!settings || settings.enable_google_routes !== true) {
        return false;
    }

    const allowedReasons = ['booking_confirmed', 'booking_estimate', 'admin_estimate', 'driver_accepted'];
    if (!allowedReasons.includes(routeReason)) {
        return false;
    }

    if (!routePolylinePoints || !lastRouteCalcAt) {
        return true;
    }

    if (Date.now() - lastRouteCalcAt < 5 * 60 * 1000) {
        return false;
    }

    if (!driverLocation || !context.nearestRoutePoint) {
        return false;
    }

    const deviationKm = calculateHaversineDistanceKm(driverLocation, context.nearestRoutePoint);
    return deviationKm * 1000 >= deviationMeters;
};

const toMapCoord = (point) => {
    if (!point) return null;
    const lat = typeof point.lat === 'number' ? point.lat : parseFloat(point.latitude);
    const lng = typeof point.lng === 'number' ? point.lng : parseFloat(point.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { latitude: lat, longitude: lng };
};

/** Une puntos GPS históricos + posición actual + destino (sin Google Routes). */
export const buildLiveRouteCoordsFromTrail = (trail = [], current = null, destination = null) => {
    const coords = [];
    const pushCoord = (point) => {
        const mapped = toMapCoord(point);
        if (!mapped) return;
        const last = coords[coords.length - 1];
        if (last && last.latitude === mapped.latitude && last.longitude === mapped.longitude) return;
        coords.push(mapped);
    };

    if (Array.isArray(trail)) {
        trail.forEach((point) => pushCoord(point));
    }
    pushCoord(current);
    pushCoord(destination);

    if (coords.length >= 2) return coords;
    const from = toMapCoord(current);
    const to = toMapCoord(destination);
    if (from && to) return [from, to];
    return coords;
};

export const buildDriverLocationPayload = (coords, meta = {}) => ({
    lat: coords.lat,
    lng: coords.lng,
    geohash: encodeGeohash(coords.lat, coords.lng, 7),
    heading: coords.heading != null ? normalizeHeading(coords.heading) : null,
    speed: coords.speed != null ? coords.speed : null,
    updatedAt: Date.now(),
    isOnline: meta.isOnline !== false,
    isAvailable: meta.isAvailable !== false,
    isOnTrip: !!meta.isOnTrip,
    bookingId: meta.bookingId || null,
    vehicleType: meta.vehicleType || null,
    status: meta.status || (meta.isOnTrip ? 'on_trip' : 'available'),
});
